import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useFeatureFlags,
  useCreateFeatureFlag,
  useUpdateFeatureFlag,
  useDeleteFeatureFlag,
} from '../../hooks/useAdminExtended';
import type { FeatureFlag } from '../../api/client';

const PLANS = ['FREE', 'PRO', 'ENTERPRISE'] as const;

interface FlagFormData {
  name: string;
  description: string;
  isEnabled: boolean;
  targetPlans: string[];
}

const emptyForm: FlagFormData = {
  name: '',
  description: '',
  isEnabled: false,
  targetPlans: [],
};

export default function FeatureFlagsPage() {
  const { data: flags, isLoading, error } = useFeatureFlags();
  const createMutation = useCreateFeatureFlag();
  const updateMutation = useUpdateFeatureFlag();
  const deleteMutation = useDeleteFeatureFlag();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [form, setForm] = useState<FlagFormData>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleOpenCreate = () => {
    setEditingFlag(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (flag: FeatureFlag) => {
    setEditingFlag(flag);
    setForm({
      name: flag.name,
      description: flag.description ?? '',
      isEnabled: flag.isEnabled,
      targetPlans: Array.isArray(flag.targetPlans) ? flag.targetPlans : [],
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingFlag(null);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    const payload = {
      name: form.name,
      description: form.description || undefined,
      isEnabled: form.isEnabled,
      targetPlans: form.targetPlans.length > 0 ? form.targetPlans : null,
    };

    if (editingFlag) {
      await updateMutation.mutateAsync({ id: editingFlag.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    handleClose();
  };

  const handleToggle = async (flag: FeatureFlag) => {
    await updateMutation.mutateAsync({
      id: flag.id,
      data: { isEnabled: !flag.isEnabled },
    });
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirmId(null);
  };

  const handlePlanToggle = (plan: string) => {
    setForm((prev) => ({
      ...prev,
      targetPlans: prev.targetPlans.includes(plan)
        ? prev.targetPlans.filter((p) => p !== plan)
        : [...prev.targetPlans, plan],
    }));
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load feature flags.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Feature Flags
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ minHeight: 44 }}
        >
          Add Flag
        </Button>
      </Box>

      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={120} />
          ))}
        </Stack>
      ) : flags && flags.length > 0 ? (
        <Stack spacing={2}>
          {flags.map((flag) => (
            <Card key={flag.id}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: flag.isEnabled ? 'success.main' : 'grey.400',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: 15, sm: 16 },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {flag.name}
                      </Typography>
                    </Box>
                    {flag.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontSize: { xs: 13, sm: 14 } }}
                      >
                        {flag.description}
                      </Typography>
                    )}
                    {Array.isArray(flag.targetPlans) && flag.targetPlans.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {flag.targetPlans.map((plan) => (
                          <Chip
                            key={plan}
                            label={plan}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 12 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                    <Switch
                      checked={flag.isEnabled}
                      onChange={() => handleToggle(flag)}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(flag)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteConfirmId(flag.id)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 6 }}
        >
          No feature flags yet. Create one to get started.
        </Typography>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingFlag ? 'Edit Feature Flag' : 'Create Feature Flag'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Flag Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              fullWidth
              multiline
              rows={2}
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isEnabled}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, isEnabled: e.target.checked }))
                  }
                />
              }
              label="Enabled"
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Target Plans (leave unchecked for all plans)
              </Typography>
              <FormGroup row>
                {PLANS.map((plan) => (
                  <FormControlLabel
                    key={plan}
                    control={
                      <Checkbox
                        checked={form.targetPlans.includes(plan)}
                        onChange={() => handlePlanToggle(plan)}
                        size="small"
                      />
                    }
                    label={plan}
                  />
                ))}
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ minHeight: 44 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !form.name.trim() ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            sx={{ minHeight: 44 }}
          >
            {editingFlag ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Feature Flag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this feature flag? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirmId(null)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            disabled={deleteMutation.isPending}
            sx={{ minHeight: 44 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
